/**
 * Neon Battle Royale
 * Battle Pass booking interactions
 * Version 2.2.0
 */

(() => {
  'use strict';

  const SECTION_SELECTOR = '[data-nbr-ticket-tiers]';
  const CARD_SELECTOR = '[data-nbr-ticket-card]';
  const FORM_SELECTOR = '.nbr-ticket-card__form';
  const BUTTON_SELECTOR = '.nbr-ticket-card__button';
  const BUTTON_TEXT_SELECTOR =
    '.nbr-ticket-card__button > span:first-child';
  const STATUS_SELECTOR = '.nbr-ticket-card__status';

  const DETAILS_SELECTOR = '[data-nbr-attendee-details]';
  const INPUT_SELECTOR = '[data-nbr-attendee-input]';
  const PHONE_SELECTOR = '[data-nbr-purchaser-phone]';
  const CONFIRMATION_SELECTOR =
    '[data-nbr-attendee-confirmation]';

  const PHONE_PATTERN = /^[6-9][0-9]{9}$/;

  const initializedForms = new WeakSet();
  const formControllers = new WeakMap();

  const injectStabilityStyles = () => {
    if (
      document.getElementById(
        'NBRStableTicketInputStyles'
      )
    ) {
      return;
    }

    const style = document.createElement('style');

    style.id = 'NBRStableTicketInputStyles';

    style.textContent = `
      .nbr-ticket-card.nbr-ticket-card--input-stable,
      .nbr-ticket-card.nbr-ticket-card--input-stable:hover,
      .nbr-ticket-card.nbr-ticket-card--input-stable:focus,
      .nbr-ticket-card.nbr-ticket-card--input-stable:focus-within {
        transform: none !important;
        animation: none !important;
        transition:
          border-color 180ms ease,
          box-shadow 180ms ease,
          opacity 180ms ease !important;
      }

      .nbr-ticket-card--input-stable
      .nbr-ticket-card__shine {
        display: none !important;
      }

      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees,
      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__body,
      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__fields,
      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__field,
      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__confirmation,
      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__phone-field,
      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__input {
        transform: none !important;
        animation: none !important;
        contain: none !important;
        content-visibility: visible !important;
      }

      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__input {
        position: relative !important;
        z-index: 20 !important;
        width: 100% !important;
        min-width: 0 !important;
        opacity: 1 !important;
        visibility: visible !important;
        pointer-events: auto !important;
        touch-action: manipulation !important;
        user-select: text !important;
        -webkit-user-select: text !important;
        caret-color: #ffffff !important;
        transform: none !important;
        animation: none !important;
      }

      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__input:focus {
        transform: none !important;
        animation: none !important;
      }

      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__phone-field {
        position: relative !important;
        z-index: 19 !important;
      }

      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__phone-field:focus-within {
        border-color: #00e5ff !important;
        box-shadow:
          0 0 0 3px rgba(0, 229, 255, 0.14) !important;
      }

      .nbr-ticket-card--input-stable
      .nbr-ticket-attendees__field.has-field-error
      .nbr-ticket-attendees__phone-field {
        border-color: #ff4d78 !important;
        box-shadow:
          0 0 0 3px rgba(255, 77, 120, 0.14) !important;
      }

      .nbr-ticket-card--input-stable.is-form-focused {
        transform: none !important;
        animation: none !important;
      }

      @media (hover: hover) and (pointer: fine) {
        .nbr-ticket-card--input-stable:hover {
          transform: none !important;
        }
      }

      @media (max-width: 749px) {
        .nbr-ticket-card--input-stable
        .nbr-ticket-attendees__input {
          font-size: 16px !important;
        }
      }
    `;

    document.head.appendChild(style);
  };

  const getShopifyRoot = () => {
    const root =
      window.Shopify?.routes?.root || '/';

    return root.endsWith('/')
      ? root
      : `${root}/`;
  };

  const getCartUrl = () =>
    `${getShopifyRoot()}cart`;

  const getCartAddUrl = () =>
    `${getShopifyRoot()}cart/add.js`;

  const getCartJsonUrl = () =>
    `${getShopifyRoot()}cart.js`;

  const prefersReducedMotion = () =>
    window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

  const setButtonState = (
    button,
    textElement,
    text,
    state = 'idle'
  ) => {
    if (!button) {
      return;
    }

    button.dataset.nbrState = state;

    if (textElement) {
      textElement.textContent = text;
    }

    if (state === 'loading') {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      return;
    }

    button.disabled = false;
    button.removeAttribute('aria-busy');
  };

  const updateStatus = (
    statusElement,
    message,
    state = 'default'
  ) => {
    if (!statusElement) {
      return;
    }

    statusElement.replaceChildren();

    const dot = document.createElement('span');
    const text = document.createElement('span');

    dot.className =
      'nbr-ticket-card__status-dot';

    if (state === 'error') {
      dot.classList.add(
        'nbr-ticket-card__status-dot--closed'
      );
    }

    dot.setAttribute('aria-hidden', 'true');
    text.textContent = message;

    statusElement.append(dot, text);
    statusElement.dataset.nbrStatus = state;
    statusElement.setAttribute('role', 'status');
    statusElement.setAttribute(
      'aria-live',
      'polite'
    );
  };

  const stabilizeElement = (element) => {
    if (!element) {
      return;
    }

    [
      'data-nbr-reveal',
      'data-nbr-pointer-effect',
      'data-nbr-tilt',
      'data-nbr-parallax'
    ].forEach((attribute) => {
      element.removeAttribute(attribute);
    });

    element.style.setProperty(
      'transform',
      'none',
      'important'
    );

    element.style.setProperty(
      'animation',
      'none',
      'important'
    );
  };

  const stabilizeCard = (card, form) => {
    if (!card || !form) {
      return;
    }

    card.classList.add(
      'nbr-ticket-card--input-stable'
    );

    stabilizeElement(card);
    stabilizeElement(form);

    card
      .querySelectorAll(
        [
          '[data-nbr-reveal]',
          '[data-nbr-pointer-effect]',
          '[data-nbr-tilt]',
          '[data-nbr-parallax]'
        ].join(',')
      )
      .forEach(stabilizeElement);

    form
      .querySelectorAll(
        `${INPUT_SELECTOR}, ${CONFIRMATION_SELECTOR}`
      )
      .forEach(stabilizeElement);
  };

  const clearFieldError = (field) => {
    field.removeAttribute('aria-invalid');

    field
      .closest(
        '.nbr-ticket-attendees__field, ' +
          '.nbr-ticket-attendees__confirmation'
      )
      ?.classList.remove('has-field-error');

    const details = field.closest(
      DETAILS_SELECTOR
    );

    if (
      details &&
      !details.querySelector(
        '[aria-invalid="true"]'
      )
    ) {
      details.classList.remove(
        'has-attendee-error'
      );
    }
  };

  const clearValidation = (form) => {
    form
      .querySelectorAll('[aria-invalid="true"]')
      .forEach(clearFieldError);

    form
      .querySelectorAll(DETAILS_SELECTOR)
      .forEach((details) => {
        details.classList.remove(
          'has-attendee-error'
        );
      });
  };

  const markInvalid = (field) => {
    const details = field.closest(
      DETAILS_SELECTOR
    );

    if (details) {
      details.open = true;
      details.classList.add(
        'has-attendee-error'
      );
    }

    field
      .closest(
        '.nbr-ticket-attendees__field, ' +
          '.nbr-ticket-attendees__confirmation'
      )
      ?.classList.add('has-field-error');

    field.setAttribute(
      'aria-invalid',
      'true'
    );
  };

  const focusField = (
    field,
    scrollTarget = null
  ) => {
    if (!field) {
      return;
    }

    const target =
      scrollTarget ||
      field.closest(DETAILS_SELECTOR) ||
      field;

    window.requestAnimationFrame(() => {
      target.scrollIntoView({
        behavior: prefersReducedMotion()
          ? 'auto'
          : 'smooth',
        block: 'center'
      });

      window.setTimeout(() => {
        try {
          field.focus({
            preventScroll: true
          });
        } catch (error) {
          field.focus();
        }
      }, prefersReducedMotion() ? 0 : 260);
    });
  };

  const revealBookingDetails = (
    form,
    controller,
    button,
    buttonText,
    status
  ) => {
    const details = form.querySelector(
      DETAILS_SELECTOR
    );

    if (!details) {
      return false;
    }

    details.open = true;
    details.dataset.nbrBookingOpened = 'true';

    controller.detailsRevealed = true;

    setButtonState(
      button,
      buttonText,
      'Add Battle Pass to Cart',
      'idle'
    );

    updateStatus(
      status,
      'Enter the purchaser mobile number and attendee names to continue.',
      'default'
    );

    const firstField =
      details.querySelector(PHONE_SELECTOR) ||
      details.querySelector(INPUT_SELECTOR) ||
      details.querySelector('[required]');

    focusField(firstField, details);

    return true;
  };

  const sanitizePhoneValue = (field) => {
    const originalValue =
      String(field.value || '');

    let digits = originalValue.replace(
      /[^0-9]/g,
      ''
    );

    if (
      digits.length === 12 &&
      digits.startsWith('91')
    ) {
      digits = digits.slice(2);
    }

    digits = digits.slice(0, 10);

    if (field.value !== digits) {
      field.value = digits;
    }

    return digits;
  };

  const getFieldValidationMessage = (
    field
  ) => {
    if (
      field instanceof HTMLInputElement &&
      field.type === 'checkbox'
    ) {
      return field.checked
        ? ''
        : 'Confirm the attendee details before booking.';
    }

    if (field.matches(PHONE_SELECTOR)) {
      const value = sanitizePhoneValue(field);

      if (!value) {
        return (
          'Enter the purchaser mobile number ' +
          'before booking.'
        );
      }

      if (value.length !== 10) {
        return (
          'Enter exactly 10 digits for the ' +
          'purchaser mobile number.'
        );
      }

      if (!PHONE_PATTERN.test(value)) {
        return (
          'Enter a valid Indian mobile number ' +
          'beginning with 6, 7, 8 or 9.'
        );
      }

      return '';
    }

    const value =
      String(field.value || '')
        .trim()
        .replace(/\s+/g, ' ');

    field.value = value;

    if (!value) {
      return (
        'Complete all required attendee ' +
        'details before booking.'
      );
    }

    const minimumLength = Number.parseInt(
      field.getAttribute('minlength'),
      10
    );

    if (
      Number.isFinite(minimumLength) &&
      value.length < minimumLength
    ) {
      return (
        'Enter complete and accurate attendee ' +
        'details before booking.'
      );
    }

    return '';
  };

  const validateForm = (form) => {
    clearValidation(form);

    const requiredFields = Array.from(
      form.querySelectorAll('[required]')
    );

    let firstInvalid = null;
    let firstMessage = '';

    requiredFields.forEach((field) => {
      const message =
        getFieldValidationMessage(field);

      if (!message) {
        return;
      }

      markInvalid(field);

      if (!firstInvalid) {
        firstInvalid = field;
        firstMessage = message;
      }
    });

    if (firstInvalid) {
      focusField(firstInvalid);

      return {
        isValid: false,
        message: firstMessage
      };
    }

    return {
      isValid: true,
      message: ''
    };
  };

  const getErrorMessage = (
    payload,
    response
  ) => {
    if (payload?.description) {
      return payload.description;
    }

    if (payload?.message) {
      return payload.message;
    }

    if (response?.status === 422) {
      return (
        'This Battle Pass is unavailable ' +
        'or has sold out.'
      );
    }

    return (
      'This Battle Pass could not be added. ' +
      'Please try again.'
    );
  };

  const addToCart = async (
    form,
    signal
  ) => {
    const response = await fetch(
      getCartAddUrl(),
      {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With':
            'XMLHttpRequest'
        },
        body: new FormData(form),
        signal
      }
    );

    let payload = null;

    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok) {
      throw new Error(
        getErrorMessage(payload, response)
      );
    }

    return payload;
  };

  const fetchCart = async (signal) => {
    const response = await fetch(
      getCartJsonUrl(),
      {
        method: 'GET',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'X-Requested-With':
            'XMLHttpRequest'
        },
        signal
      }
    );

    if (!response.ok) {
      throw new Error(
        'The cart could not be refreshed.'
      );
    }

    return response.json();
  };

  const updateCartCounters = (itemCount) => {
    const count = Number.parseInt(
      itemCount,
      10
    );

    if (!Number.isFinite(count)) {
      return;
    }

    const selectors = [
      '[data-cart-count]',
      '[data-nbr-cart-count]',
      '[data-cart-item-count]',
      '.cart-count-bubble span[aria-hidden="true"]'
    ];

    document
      .querySelectorAll(selectors.join(','))
      .forEach((counter) => {
        counter.textContent = String(count);
        counter.dataset.cartCount =
          String(count);
      });

    document.documentElement.dataset
      .cartItemCount = String(count);
  };

  const dispatchCartEvents = (
    cart,
    addedItem,
    form
  ) => {
    const detail = {
      cart,
      addedItem,
      source: 'nbr-ticket-tiers',
      form
    };

    document.dispatchEvent(
      new CustomEvent(
        'nbr:cart:updated',
        {
          bubbles: true,
          detail
        }
      )
    );

    document.dispatchEvent(
      new CustomEvent(
        'cart:updated',
        {
          bubbles: true,
          detail
        }
      )
    );
  };

  const resetSuccessfulState = (
    form,
    controller
  ) => {
    if (!controller.addedSuccessfully) {
      return;
    }

    controller.addedSuccessfully = false;

    const card = form.closest(CARD_SELECTOR);
    const button = form.querySelector(
      BUTTON_SELECTOR
    );
    const buttonText =
      button?.querySelector(
        BUTTON_TEXT_SELECTOR
      );

    card?.classList.remove('is-added');

    setButtonState(
      button,
      buttonText,
      controller.detailsRevealed
        ? 'Add Battle Pass to Cart'
        : controller.originalButtonText,
      'idle'
    );
  };

  const registerListener = (
    controller,
    element,
    eventType,
    handler,
    options
  ) => {
    element.addEventListener(
      eventType,
      handler,
      options
    );

    controller.listeners.push({
      element,
      eventType,
      handler,
      options
    });
  };

  const initializeForm = (form) => {
    if (
      !form ||
      initializedForms.has(form)
    ) {
      return;
    }

    const card = form.closest(CARD_SELECTOR);
    const button = form.querySelector(
      BUTTON_SELECTOR
    );
    const buttonText =
      button?.querySelector(
        BUTTON_TEXT_SELECTOR
      );
    const status = card?.querySelector(
      STATUS_SELECTOR
    );
    const details = form.querySelector(
      DETAILS_SELECTOR
    );

    if (!card || !button) {
      return;
    }

    injectStabilityStyles();
    stabilizeCard(card, form);

    form.noValidate = true;

    const controller = {
      abortController: null,
      isSubmitting: false,
      addedSuccessfully: false,
      detailsRevealed:
        Boolean(details?.open),
      listeners: [],
      originalButtonText:
        buttonText?.textContent?.trim() ||
        'Book Your Battle Pass'
    };

    initializedForms.add(form);
    formControllers.set(form, controller);

    form
      .querySelectorAll(PHONE_SELECTOR)
      .forEach(sanitizePhoneValue);

    form
      .querySelectorAll(
        `${INPUT_SELECTOR}, ${CONFIRMATION_SELECTOR}`
      )
      .forEach((field) => {
        const inputHandler = (event) => {
          event.stopPropagation();

          if (field.matches(PHONE_SELECTOR)) {
            sanitizePhoneValue(field);
          }

          clearFieldError(field);
          resetSuccessfulState(
            form,
            controller
          );
        };

        registerListener(
          controller,
          field,
          'input',
          inputHandler
        );

        registerListener(
          controller,
          field,
          'change',
          inputHandler
        );

        registerListener(
          controller,
          field,
          'keydown',
          (event) => {
            event.stopPropagation();
          }
        );

        registerListener(
          controller,
          field,
          'keyup',
          (event) => {
            event.stopPropagation();
          }
        );

        registerListener(
          controller,
          field,
          'paste',
          (event) => {
            event.stopPropagation();

            window.requestAnimationFrame(() => {
              if (field.matches(PHONE_SELECTOR)) {
                sanitizePhoneValue(field);
              }

              clearFieldError(field);
              resetSuccessfulState(
                form,
                controller
              );
            });
          }
        );
      });

    if (details) {
      const toggleHandler = () => {
        controller.detailsRevealed =
          details.open;

        if (
          controller.isSubmitting ||
          controller.addedSuccessfully
        ) {
          return;
        }

        if (details.open) {
          setButtonState(
            button,
            buttonText,
            'Add Battle Pass to Cart',
            'idle'
          );

          updateStatus(
            status,
            'Complete the required booking details below.',
            'default'
          );
        } else {
          setButtonState(
            button,
            buttonText,
            controller.originalButtonText,
            'idle'
          );
        }
      };

      registerListener(
        controller,
        details,
        'toggle',
        toggleHandler
      );
    }

    const focusInHandler = (event) => {
      if (
        !event.target.matches?.(
          'input, textarea, select'
        )
      ) {
        return;
      }

      event.stopPropagation();

      card.classList.add(
        'is-form-focused'
      );

      stabilizeCard(card, form);
    };

    registerListener(
      controller,
      form,
      'focusin',
      focusInHandler
    );

    const focusOutHandler = () => {
      window.requestAnimationFrame(() => {
        if (
          form.contains(
            document.activeElement
          )
        ) {
          return;
        }

        card.classList.remove(
          'is-form-focused'
        );
      });
    };

    registerListener(
      controller,
      form,
      'focusout',
      focusOutHandler
    );

    const submitHandler = async (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (
        controller.addedSuccessfully &&
        button.dataset.nbrState ===
          'success'
      ) {
        window.location.assign(
          getCartUrl()
        );
        return;
      }

      if (controller.isSubmitting) {
        return;
      }

      if (details && !details.open) {
        revealBookingDetails(
          form,
          controller,
          button,
          buttonText,
          status
        );

        return;
      }

      const validation =
        validateForm(form);

      if (!validation.isValid) {
        card.classList.add(
          'has-ticket-error'
        );

        updateStatus(
          status,
          validation.message,
          'error'
        );

        return;
      }

      controller.isSubmitting = true;

      controller.abortController?.abort();
      controller.abortController =
        new AbortController();

      card.classList.remove(
        'has-ticket-error',
        'is-added'
      );

      card.classList.add('is-adding');

      setButtonState(
        button,
        buttonText,
        'Adding Pass…',
        'loading'
      );

      updateStatus(
        status,
        'Securing your Battle Pass…',
        'default'
      );

      try {
        const addedItem = await addToCart(
          form,
          controller.abortController.signal
        );

        const cart = await fetchCart(
          controller.abortController.signal
        );

        controller.addedSuccessfully = true;

        card.classList.remove(
          'is-adding',
          'has-ticket-error'
        );

        card.classList.add('is-added');

        setButtonState(
          button,
          buttonText,
          'View Cart',
          'success'
        );

        updateStatus(
          status,
          'Battle Pass and attendee details added successfully.',
          'success'
        );

        updateCartCounters(
          cart.item_count
        );

        dispatchCartEvents(
          cart,
          addedItem,
          form
        );
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }

        controller.addedSuccessfully = false;

        card.classList.remove(
          'is-adding',
          'is-added'
        );

        card.classList.add(
          'has-ticket-error'
        );

        setButtonState(
          button,
          buttonText,
          'Add Battle Pass to Cart',
          'error'
        );

        updateStatus(
          status,
          error?.message ||
            'This Battle Pass could not be added. Please try again.',
          'error'
        );
      } finally {
        controller.isSubmitting = false;
        controller.abortController = null;

        if (
          button.dataset.nbrState ===
          'loading'
        ) {
          setButtonState(
            button,
            buttonText,
            'Add Battle Pass to Cart',
            'idle'
          );
        }
      }
    };

    registerListener(
      controller,
      form,
      'submit',
      submitHandler
    );
  };

  const initializeForms = (
    scope = document
  ) => {
    if (scope.matches?.(FORM_SELECTOR)) {
      initializeForm(scope);
    }

    scope
      .querySelectorAll?.(FORM_SELECTOR)
      .forEach(initializeForm);
  };

  const destroyForms = (scope) => {
    const forms = [];

    if (scope.matches?.(FORM_SELECTOR)) {
      forms.push(scope);
    }

    scope
      .querySelectorAll?.(FORM_SELECTOR)
      .forEach((form) => {
        forms.push(form);
      });

    forms.forEach((form) => {
      const controller =
        formControllers.get(form);

      controller?.abortController?.abort();

      controller?.listeners.forEach(
        ({
          element,
          eventType,
          handler,
          options
        }) => {
          element.removeEventListener(
            eventType,
            handler,
            options
          );
        }
      );

      formControllers.delete(form);
      initializedForms.delete(form);
    });
  };

  const initialize = () => {
    injectStabilityStyles();

    document
      .querySelectorAll(SECTION_SELECTOR)
      .forEach((section) => {
        initializeForms(section);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialize,
      {
        once: true
      }
    );
  } else {
    initialize();
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeForms(event.target);
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      destroyForms(event.target);
    }
  );
})();